import React, { useMemo, useState } from 'react';

import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useCustomerServiceRepMemberCounts } from '~/hooks/apis/loyalty/useCustomerServiceRepAssignments';
import { useGetCustomerServiceReps } from '~/hooks/apis/loyalty/useCustomerServiceReps';

import type { CustomerServiceRep } from '~/services/LuxLoyalty';

import CustomerServiceRepFormDialog from './CustomerServiceRepFormDialog';
import CustomerServiceRepRemoveDialog from './CustomerServiceRepRemoveDialog';

const REPS_PAGE_SIZE = 50;

function repInitials(rep: CustomerServiceRep): string {
  return `${rep.firstName.charAt(0)}${rep.lastName.charAt(0)}`.toUpperCase();
}

/** Configurator-style list of reps with create/edit/remove actions. */
export default function RepresentativesTab() {
  const repsQuery = useGetCustomerServiceReps({ page: 1, pageSize: REPS_PAGE_SIZE });

  const [formOpen, setFormOpen] = useState(false);
  const [editingRep, setEditingRep] = useState<CustomerServiceRep | undefined>(undefined);
  const [removingRep, setRemovingRep] = useState<CustomerServiceRep | null>(null);

  const reps = repsQuery.data?.result.reps ?? [];
  const repIds = useMemo(() => reps.map((rep) => rep.id), [reps]);
  const { countsById: memberCounts, isLoading: countsLoading } = useCustomerServiceRepMemberCounts(repIds);

  const existingEmails = reps.map((rep) => rep.email);

  const openCreate = () => {
    setEditingRep(undefined);
    setFormOpen(true);
  };

  const openEdit = (rep: CustomerServiceRep) => {
    setEditingRep(rep);
    setFormOpen(true);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Representatives
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Each rep is created once. Their details stay consistent across their entire portfolio.
          </Typography>
        </Box>
        <Button
          variant="contained"
          disableElevation
          color="primary"
          onClick={openCreate}
          sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 700, px: 2.5 }}
        >
          Add representative
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        Name, mobile and email are shown to members on their Infinite landing page. Editing a rep updates every member
        in their portfolio at once.
      </Alert>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={(theme) => ({
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          overflow: 'hidden',
          '& thead .MuiTableCell-root': {
            bgcolor: theme.palette.action.hover,
            fontWeight: 700,
            color: 'text.secondary',
            textTransform: 'uppercase',
            fontSize: 12,
            letterSpacing: 0.6,
          },
        })}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Representative</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell align="right">Members</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {repsQuery.isLoading && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            )}
            {repsQuery.isError && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Alert severity="error">Failed to load representatives.</Alert>
                </TableCell>
              </TableRow>
            )}
            {!repsQuery.isLoading && !repsQuery.isError && reps.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>
                    No representatives yet. Click <strong>Add representative</strong> to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {reps.map((rep) => {
              const count = memberCounts[rep.id] ?? 0;
              return (
                <TableRow key={rep.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        sx={(theme) => ({
                          width: 32,
                          height: 32,
                          fontSize: 13,
                          fontWeight: 700,
                          background: theme.palette.loyaltyTiers.infinite.gradient,
                          color: 'common.white',
                        })}
                      >
                        {repInitials(rep)}
                      </Avatar>
                      <Typography variant="body2" fontWeight={600}>
                        {rep.firstName} {rep.lastName}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{rep.email}</TableCell>
                  <TableCell>{rep.phone}</TableCell>
                  <TableCell align="right">
                    {countsLoading ? <CircularProgress size={14} /> : <Chip size="small" label={count} />}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                      <MuiLink component="button" type="button" variant="body2" onClick={() => openEdit(rep)}>
                        Edit
                      </MuiLink>
                      <MuiLink
                        component="button"
                        type="button"
                        variant="body2"
                        color="error"
                        onClick={() => setRemovingRep(rep)}
                        disabled={countsLoading}
                      >
                        Remove
                      </MuiLink>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomerServiceRepFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        rep={editingRep}
        existingEmails={existingEmails}
      />

      <CustomerServiceRepRemoveDialog
        open={!!removingRep}
        onClose={() => setRemovingRep(null)}
        rep={removingRep}
        otherReps={reps.filter((other) => other.id !== removingRep?.id)}
        mappedCustomerCount={removingRep ? (memberCounts[removingRep.id] ?? 0) : 0}
      />
    </Box>
  );
}
